import { Database, Lock, ShieldCheck, Users } from 'lucide-react'
import type { Layer } from '@nw/shared/layers'
import { formatDate } from '@nw/shared/messages/index'
import { useLocale, useT } from '../lib/i18n.tsx'
import { Chip, type Tone } from './Chip.tsx'
import { Tooltip } from './Tooltip.tsx'
import { cn } from './util.ts'

export type Provenance = 'REGISTER' | 'ENRICHED' | 'IMPORTED' | 'ASSERTED' | 'INFERRED'
export type Phase = 'P1' | 'P2' | 'P3' | 'P4'
/** The layers a screen shows data from (suppression and admin are never shown as a badge). */
export type VisibleLayer = Extract<Layer, 'shared' | 'mine' | 'register' | 'mirror'>

const provenanceTone: Record<Provenance, Tone> = {
  REGISTER: 'register',
  ENRICHED: 'team',
  IMPORTED: 'private',
  ASSERTED: 'ok',
  INFERRED: 'warn',
}

export interface ProvenanceChipProps {
  provenance: Provenance
  /** The named source, e.g. 'Enhetsregisteret'. */
  source?: string
  /** Date of the fact, YYYY-MM-DD. */
  asOf?: string
  className?: string
}

/** Where a fact comes from: <ProvenanceChip provenance="REGISTER" source="…" asOf="2026-10-14" />. */
export function ProvenanceChip({ provenance: p, source, asOf, className }: ProvenanceChipProps) {
  const { t, locale } = useLocale()
  const tip = [t(`common.provenanceHint.${p}`), source, asOf && t('common.asOf', { date: formatDate(locale, asOf) })].filter(Boolean).join(' · ')
  return (
    <Tooltip content={tip}>
      <button type="button" aria-label={`${t(`common.provenance.${p}`)}: ${tip}`} className={cn('inline-flex cursor-default rounded-sm', className)}>
        <Chip tone={provenanceTone[p]} variant="soft" mono dashed={p === 'INFERRED'}>
          {p}
        </Chip>
      </button>
    </Tooltip>
  )
}

const layers: Record<VisibleLayer, { tone: Tone; icon: typeof Users }> = {
  shared: { tone: 'team', icon: Users },
  mine: { tone: 'private', icon: Lock },
  register: { tone: 'register', icon: ShieldCheck },
  mirror: { tone: 'neutral', icon: Database },
}

/** Which layer holds the data, outlined: <LayerBadge layer="mine" /> reads "Only you". */
export function LayerBadge({ layer, className }: { layer: VisibleLayer; className?: string }) {
  const t = useT()
  const meta = layers[layer]
  return (
    <span title={t(`common.layerHint.${layer}`)} className={cn('inline-flex', className)}>
      <Chip tone={meta.tone} variant="outline" icon={meta.icon} mono>
        {t(`common.layerBadge.${layer}`)}
      </Chip>
    </span>
  )
}

/** Plan phase of a feature: <PhaseBadge phase="P4" />. P1 is outlined; P2 to P4 share the soft accent chip. */
export function PhaseBadge({ phase, className }: { phase: Phase; className?: string }) {
  const t = useT()
  return (
    <span title={t(`common.phaseHint.${phase}`)} className={cn('inline-flex', className)}>
      <Chip tone={phase === 'P1' ? 'neutral' : 'accent'} variant={phase === 'P1' ? 'outline' : 'soft'} mono>
        {phase}
      </Chip>
    </span>
  )
}
